<script setup>

import { computed, onMounted, ref } from 'vue'
import axios from 'axios'
import LatteContainer from '@/Components/App/LatteContainer.vue'
import Loader from '@/Components/App/Loader.vue'
import { useAuthStore, useNotificationStore } from '@/Stores'
import { storeToRefs } from 'pinia'

const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const notificationStore = useNotificationStore()

const isAdmin = computed(() => user.value?.user_id === 1)

const ADMIN_ONLY_LATTE_ITEMS = ['Client QB Id Linkage', 'Caregiver QB Id Linkage']

const latteDataRaw = ref([])
const invoicingData = ref({})
const qbTableRows = ref([])
const regionDisplayOrder = ref([])
const loading = ref(true)
const expandedRegions = ref({})

const COLOR_CLASS_MAP = {
    red: 'bg-lifeworx-red-500',
    yellow: 'bg-lifeworx-yellow-500',
    green: 'bg-lifeworx-green-500',
}

const latteData = computed(() => {
    if (isAdmin.value) {
        return latteDataRaw.value
    }
    return latteDataRaw.value.filter(item => !ADMIN_ONLY_LATTE_ITEMS.includes(item.name))
})

onMounted(async () => {
    await fetchSyncStatus()
})

async function fetchSyncStatus() {
    loading.value = true
    try {
        const response = await axios.get('/api/v1/status/quickbooks')
        latteDataRaw.value = response.data.latte ?? []
        invoicingData.value = response.data.invoicing ?? {}
        qbTableRows.value = response.data.qb_table_rows ?? []
        regionDisplayOrder.value = response.data.region_display_order ?? []
    } catch (error) {
        notificationStore.add('Error', 'error', 'Failed to fetch QuickBooks sync status', 5000)
        console.error('Fetch sync status error:', error)
    } finally {
        loading.value = false
    }
}

function getStatusInfo(timestamp) {
    if (!timestamp) {
        return {
            label: 'No data',
            className: 'bg-gray-100 text-gray-800',
        }
    }

    const updatedAt = new Date(timestamp)
    const diffMs = Date.now() - updatedAt.getTime()
    const diffHours = diffMs / (1000 * 60 * 60)

    let label
    if (diffHours >= 1) {
        const rounded = Math.floor(diffHours)
        const hourLabel = rounded === 1 ? 'hour' : 'hours'
        label = `Synced ${rounded} ${hourLabel} ago`
    } else {
        label = 'Synced within the last hour'
    }

    if (diffHours < 12) {
        return {
            label: label,
            className: 'bg-green-100 text-lifeworx-green-800',
        }
    }

    return {
        label: label,
        className: 'bg-lifeworx-red-100 text-lifeworx-red-800',
    }
}

function formatTimestamp(timestamp) {
    if (!timestamp) {
        return 'No data'
    }

    return new Date(timestamp).toLocaleString()
}

function getSortedRegions(invoicing) {
    const orderedRegions = regionDisplayOrder.value.filter(region => region in invoicing)
    const remainingRegions = Object.keys(invoicing)
        .filter(region => !regionDisplayOrder.value.includes(region))
        .sort()
    return [...orderedRegions, ...remainingRegions]
}

function toggleRegion(region) {
    expandedRegions.value[region] = !expandedRegions.value[region]
}

function formatList(items) {
    if (!items || items.length === 0) {
        return 'None'
    }
    return items.join(', ')
}

function getColorClass(color) {
    return COLOR_CLASS_MAP[color] || 'bg-gray-300'
}

const MIN_REGION_BAR_PERCENT = 2

function getRegionBarPercent(count) {
    if (!globalMaxRegionCount.value) {
        return 0
    }
    return Math.max((count / globalMaxRegionCount.value) * 100, MIN_REGION_BAR_PERCENT)
}

const oldestSyncTimestamp = computed(() => {
    const allTimestamps = []

    latteDataRaw.value.forEach(item => {
        if (item.last_successful_timestamp) {
            allTimestamps.push(item.last_successful_timestamp)
        }
    })

    Object.values(invoicingData.value).forEach(regionTransfers => {
        regionTransfers.forEach(item => {
            if (item.last_attempted_timestamp) {
                allTimestamps.push(item.last_attempted_timestamp)
            }
        })
    })

    if (allTimestamps.length === 0) {
        return null
    }

    return new Date(Math.min(...allTimestamps.map(ts => new Date(ts).getTime())))
})

const globalMaxRegionCount = computed(() => {
    const allCounts = qbTableRows.value.flatMap(tableRow => tableRow.regions.map(r => r.count))
    return allCounts.length ? Math.max(...allCounts) : 0
})

</script>

<template>
    <div class="px-20 pb-10">
        <h1 class="text-3xl font-bold text-lifeworx-blue-700 m-8 text-center">QuickBooks Sync Status</h1>
        <div class="mt-2 mb-8">
            <div v-if="loading" class="text-center text-gray-600">
                <Loader />
            </div>
            <div v-else>
                <p class="text-center text-gray-600 mt-2 mb-8">Last complete sync: <strong>{{ oldestSyncTimestamp ? formatTimestamp(oldestSyncTimestamp) : 'No data' }}</strong></p>
                <p
                    v-if="oldestSyncTimestamp"
                    class="text-center text-gray-600 mt-2 mb-8"
                >
                    As of {{ formatTimestamp(oldestSyncTimestamp) }}, QuickBooks data from all files has been synced, and QuickBooks updates should be reflected across all LifeWorx systems.
                </p>
            </div>
        </div>
        <div class="max-w-[1200px] space-y-8 mx-auto">
            <LatteContainer
                id="latte-quickbooks-sync-status"
                :scope-all="true"
                title="Latte (All Regions)"
                :collapsible="true"
            >
                <div class="p-8">
                    <div v-if="loading" class="flex justify-center items-center py-12">
                        <Loader />
                    </div>

                    <div v-else class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                        <table class="min-w-full divide-y divide-gray-300">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-6 py-3 text-left text-sm font-semibold text-gray-900">Data</th>
                                    <th class="px-6 py-3 text-left text-sm font-semibold text-gray-900">Last Sync</th>
                                    <th class="px-6 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200">
                                <tr v-for="(item, index) in latteData" :key="index" class="hover:bg-gray-50">
                                    <td class="px-6 py-4 text-sm font-medium text-gray-900">
                                        <div>{{ item.name }}</div>
                                        <div v-if="isAdmin" class="mt-1 text-xs text-gray-500 space-y-0.5">
                                            <div><span class="font-medium">Tables:</span> {{ formatList(item.qb_tables) }}</div>
                                            <div><span class="font-medium">Processes:</span> {{ formatList(item.processes) }}</div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {{ formatTimestamp(item.last_successful_timestamp) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm">
                                        <span :class="['px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full', getStatusInfo(item.last_successful_timestamp).className]">
                                            {{ getStatusInfo(item.last_successful_timestamp).label }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="!loading && latteData.length === 0" class="text-center py-12">
                        <p class="text-gray-600">No sync data available</p>
                    </div>
                </div>
            </LatteContainer>
            <LatteContainer
                id="invoicing-transfer-status"
                :scope-all="true"
                title="Invoicing"
                :collapsible="true"
            >
                <div class="p-8">
                    <div v-if="loading" class="flex justify-center items-center py-12">
                        <Loader />
                    </div>

                    <div v-else class="space-y-4">
                        <div v-for="region in getSortedRegions(invoicingData)" :key="region" class="border rounded-lg">
                            <button
                                @click="toggleRegion(region)"
                                :aria-expanded="expandedRegions[region] ? 'true' : 'false'"
                                :aria-controls="`region-panel-${region}`"
                                class="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition"
                            >
                                <h3 class="text-lg font-semibold text-gray-900">{{ region }}</h3>
                                <svg :class="['w-5 h-5 transition-transform', expandedRegions[region] ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                            </button>
                            <div v-if="expandedRegions[region]" :id="`region-panel-${region}`" class="px-6 pb-6">
                                <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                                    <table class="min-w-full divide-y divide-gray-300">
                                        <thead class="bg-gray-50">
                                            <tr>
                                                <th class="px-6 py-3 text-left text-sm font-semibold text-gray-900">Data</th>
                                                <th class="px-6 py-3 text-left text-sm font-semibold text-gray-900">Last Sync</th>
                                                <th class="px-6 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-200">
                                            <tr v-for="(transfer, index) in invoicingData[region]" :key="index" class="hover:bg-gray-50">
                                                <td class="px-6 py-4 text-sm font-medium text-gray-900">
                                                    <div>{{ transfer.name }}</div>
                                                    <div v-if="isAdmin" class="mt-1 text-xs text-gray-500 space-y-0.5">
                                                        <div><span class="font-medium">Tables:</span> {{ formatList(transfer.qb_tables) }}</div>
                                                        <div><span class="font-medium">Processes:</span> {{ formatList(transfer.processes) }}</div>
                                                    </div>
                                                </td>
                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    {{ formatTimestamp(transfer.last_attempted_timestamp) }}
                                                </td>
                                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span :class="['px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full', getStatusInfo(transfer.last_attempted_timestamp).className]">
                                                        {{ getStatusInfo(transfer.last_attempted_timestamp).label }}
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="!loading && getSortedRegions(invoicingData).length === 0" class="text-center py-12">
                        <p class="text-gray-600">No transfer data available</p>
                    </div>
                </div>
            </LatteContainer>
            <LatteContainer
                v-if="isAdmin"
                id="qb-table-rows-status"
                :scope-all="true"
                title="QB Table Rows"
                :collapsible="true"
            >
                <div class="p-8">
                    <div v-if="loading" class="flex justify-center items-center py-12">
                        <Loader />
                    </div>

                    <div v-else class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                        <table class="min-w-full divide-y divide-gray-300">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-6 py-3 text-left text-sm font-semibold text-gray-900">Table Name</th>
                                    <th class="px-6 py-3 text-left text-sm font-semibold text-gray-900">Region</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200">
                                <tr v-for="tableRow in qbTableRows" :key="tableRow.table" class="hover:bg-gray-50">
                                    <td class="px-6 py-4 text-sm font-medium text-gray-900 align-top">
                                        {{ tableRow.table }}
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-600 align-top">
                                        <div v-if="tableRow.regions.length === 0">No data</div>
                                        <div v-for="regionRow in tableRow.regions" :key="regionRow.region" class="flex items-center gap-2">
                                            <span class="inline-block w-36 text-left tabular-nums shrink-0">{{ regionRow.region }}: {{ regionRow.count.toLocaleString() }}</span>
                                            <div class="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                                                <div :class="['h-4 rounded', getColorClass(regionRow.color)]" :style="{ width: getRegionBarPercent(regionRow.count) + '%' }" />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="!loading && qbTableRows.length === 0" class="text-center py-12">
                        <p class="text-gray-600">No table data available</p>
                    </div>
                </div>
            </LatteContainer>
        </div>
    </div>
</template>
